import React, { useState, useEffect } from 'react';
import { useAppContext, useAppActions } from '../../context/AppContext';
import { 
  Shield, Plus, Search, Filter, Calendar, AlertTriangle, 
  CheckCircle, Clock, Eye, Edit3, UserCheck, Target,
  TrendingUp, TrendingDown, FileText, Download, Printer,
  RefreshCw, Bell, Award, Star, Activity, Users, 
  ClipboardList, BarChart3, AlertCircle, CheckSquare,
  FileCheck, Database, Settings, Zap, Globe, Lock,
  Brain as BrainIcon, Cpu, Activity as ActivityIcon, TrendingUp as TrendingUpIcon,
  AlertCircle as AlertCircleIcon, Target as TargetIcon, BarChart3 as BarChart3Icon,
  MessageSquare
} from 'lucide-react';
import { qualityReports, qualityIncidents, qualityMetrics } from '../../data/mockData';
import ComplianceReporting from './ComplianceReporting';
import CareMinutesDashboard from '../AgedCare/CareMinutesDashboard';
import StarRatingsDashboard from './StarRatingsDashboard';
import ComplianceDashboard from '../Compliance/ComplianceDashboard';
import NQIPDashboard from './NQIPDashboard';
import SIRSDashboard from './SIRSDashboard';
import AuditsDashboard from './AuditsDashboard';
import QualityReportsDashboard from './QualityReportsDashboard';
import IncidentsDashboard from './IncidentsDashboard';
import MetricsDashboard from './MetricsDashboard';
import OverviewDashboard from './OverviewDashboard';
import useFacilityFeatures from '../../hooks/useFacilityFeatures';

const Quality = () => {
  const { state } = useAppContext();
  const actions = useAppActions();
  const { currentUser, deviceInfo } = state;
  const { optimization } = deviceInfo;
  const { isAgedCare } = useFacilityFeatures();

  // Check if we should open care-minutes tab from navigation
  const getInitialTab = () => {
    const storedTab = sessionStorage.getItem('qualitySubTab');
    if (storedTab) {
      sessionStorage.removeItem('qualitySubTab');
      return storedTab;
    }
    return 'overview';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [searchQuery, setSearchQuery] = useState('');
  
  // Listen for navigation events from other components
  useEffect(() => {
    const handleSetSubTab = (event) => {
      if (event.detail) {
        setActiveTab(event.detail);
      }
    };
    
    window.addEventListener('setQualitySubTab', handleSetSubTab);
    return () => window.removeEventListener('setQualitySubTab', handleSetSubTab);
  }, []);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showNewReportModal, setShowNewReportModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showNQIPModal, setShowNQIPModal] = useState(false);
  const [showSIRSModal, setShowSIRSModal] = useState(false);
  const [showComplianceReporting, setShowComplianceReporting] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // AI Quality Insights Data
  const aiQualityData = {
    optimization: {
      compliance: 98.5,
      improvement: 15.2,
      efficiency: 94.3,
      costSavings: 22.1
    },
    predictions: {
      qualityIssues: 2,
      complianceRisks: 1,
      improvementOpportunities: 5,
      auditReadiness: 92
    },
    recommendations: [
      {
        id: 'QUAL_AI001',
        type: 'Compliance',
        priority: 'High',
        title: 'Address medication documentation gaps',
        description: 'AI detected 3 medication documentation inconsistencies that could impact compliance.',
        impact: 'Improve compliance score by 8%',
        category: 'Medication Management',
        status: 'Pending',
        aiConfidence: 94
      },
      {
        id: 'QUAL_AI002',
        type: 'Quality Improvement',
        priority: 'Medium',
        title: 'Implement fall prevention protocol',
        description: 'AI analysis shows fall incidents increased 12% this month. Recommend enhanced prevention measures.',
        impact: 'Reduce fall incidents by 25%',
        category: 'Patient Safety',
        status: 'Recommended',
        aiConfidence: 87
      },
      {
        id: 'QUAL_AI003',
        type: 'Efficiency',
        priority: 'Low',
        title: 'Optimize audit preparation process',
        description: 'AI suggests streamlining audit preparation to reduce preparation time by 30%.',
        impact: 'Save 15 hours per audit',
        category: 'Process Improvement',
        status: 'Suggested',
        aiConfidence: 78
      }
    ],
    analytics: {
      qualityTrends: {
        improving: 8,
        stable: 12,
        declining: 2
      },
      complianceAreas: {
        excellent: 15,
        good: 6,
        needsImprovement: 3
      },
      riskFactors: {
        high: 2,
        medium: 5,
        low: 17
      }
    }
  };

  // Enhanced mock data for NQIP, SIRS, and Audits
  const nqipData = {
    overallScore: 87.3,
    trends: {
      falls: { current: 2.1, target: 3.0, trend: -15, status: 'excellent' },
      pressureInjuries: { current: 0.8, target: 1.0, trend: -20, status: 'excellent' },
      medicationErrors: { current: 0.5, target: 1.0, trend: -25, status: 'excellent' },
      infections: { current: 3.2, target: 4.0, trend: -8, status: 'good' },
      restraintUse: { current: 1.2, target: 2.0, trend: -30, status: 'excellent' },
      weightLoss: { current: 4.1, target: 5.0, trend: -12, status: 'good' }
    },
    benchmarks: {
      national: 78.5,
      state: 82.1,
      peer: 85.7
    }
  };

  const sirsData = {
    totalReports: 23,
    thisMonth: 3,
    categories: {
      abuse: { count: 2, severity: 'high', status: 'investigating' },
      neglect: { count: 1, severity: 'medium', status: 'resolved' },
      exploitation: { count: 0, severity: 'low', status: 'none' },
      sexualAssault: { count: 0, severity: 'critical', status: 'none' }
    },
    recentReports: [
      {
        id: 'SIRS001',
        type: 'Physical Abuse',
        severity: 'High',
        date: '2024-01-15',
        resident: 'John Smith',
        facility: 'Sunshine Gardens',
        status: 'Under Investigation',
        investigator: 'Sarah Mitchell',
        description: 'Allegation of physical abuse during personal care assistance'
      },
      {
        id: 'SIRS002',
        type: 'Neglect',
        severity: 'Medium',
        date: '2024-01-10',
        resident: 'Mary Wilson',
        facility: 'Sunshine Gardens',
        status: 'Resolved',
        investigator: 'David Brown',
        description: 'Failure to provide adequate nutrition and hydration'
      }
    ]
  };

  const auditData = {
    totalAudits: 12,
    thisQuarter: 3,
    upcoming: 2,
    categories: {
      medication: { completed: 4, pending: 1, score: 94.2 },
      infectionControl: { completed: 3, pending: 1, score: 91.8 },
      residentCare: { completed: 3, pending: 0, score: 96.5 },
      safety: { completed: 2, pending: 1, score: 89.3 }
    },
    recentAudits: [
      {
        id: 'AUD001',
        type: 'Medication Management',
        date: '2024-01-25',
        auditor: 'Dr. Jennifer Lee',
        score: 94.2,
        status: 'Completed',
        findings: 2,
        recommendations: 3,
        nextAudit: '2024-04-25'
      },
      {
        id: 'AUD002',
        type: 'Infection Control',
        date: '2024-01-20',
        auditor: 'Nurse Sarah Johnson',
        score: 91.8,
        status: 'Completed',
        findings: 1,
        recommendations: 2,
        nextAudit: '2024-04-20'
      }
    ]
  };

  const showNotification = (message, type = 'info') => {
    const id = `${Date.now()}-${Math.random()}`;
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 3000);
  };

  // Calculate overall quality score from individual metrics
  const calculateOverallScore = () => {
    const metrics = Object.values(qualityMetrics);
    const totalScore = metrics.reduce((sum, metric) => sum + metric.current, 0);
    const maxScore = metrics.reduce((sum, metric) => sum + metric.benchmark, 0);
    return Math.round((totalScore / maxScore) * 100);
  };

  const overallScore = calculateOverallScore();

  const getStatusColor = (status) => {
    switch (status) {
      case 'Passed': return 'bg-green-100 text-green-800';
      case 'Under Review': return 'bg-yellow-100 text-yellow-800';
      case 'Failed': return 'bg-red-100 text-red-800';
      case 'Completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Minor': return 'bg-green-100 text-green-800';
      case 'Near Miss': return 'bg-yellow-100 text-yellow-800';
      case 'Major': return 'bg-red-100 text-red-800';
      case 'Low': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredReports = qualityReports.filter(report => {
    const matchesSearch = report.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         report.facility.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         report.assessor.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || report.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const filteredIncidents = qualityIncidents.filter(incident => {
    const matchesSearch = incident.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         incident.resident.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         incident.facility.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || incident.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white shadow-xl min-h-[120px] flex items-center">
        <div className="flex justify-between items-center w-full">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              Quality Management
              <Shield className="w-8 h-8 text-emerald-300 animate-pulse" />
            </h1>
            <p className="text-base mt-2 opacity-90">
              Monitor quality metrics and compliance standards
            </p>
          </div>
          <button 
            onClick={() => setShowNewReportModal(true)}
            className={`bg-white text-emerald-600 ${optimization.buttonSize} rounded-xl font-semibold hover:bg-opacity-90 ${optimization.animationSpeed} flex items-center gap-2`}
          >
            <Plus className={`${optimization.iconSize}`} />
            New Report
          </button>
        </div>
      </div>

      {/* Quality Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{overallScore}%</h3>
              <p className="text-sm text-gray-600">Quality Score</p>
              <div className="flex items-center gap-1 text-xs">
                <TrendingUp className="w-3 h-3 text-green-600" />
                <span className="text-green-600">+2.1%</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{qualityMetrics.medicationErrors.current}%</h3>
              <p className="text-sm text-gray-600">Medication Safety</p>
              <div className="flex items-center gap-1 text-xs">
                <TrendingUp className="w-3 h-3 text-green-600" />
                <span className="text-green-600">+1.5%</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{qualityIncidents.length}</h3>
              <p className="text-sm text-gray-600">Incidents This Month</p>
              <div className="flex items-center gap-1 text-xs">
                <TrendingDown className="w-3 h-3 text-green-600" />
                <span className="text-green-600">-15%</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Target className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{qualityReports.length}</h3>
              <p className="text-sm text-gray-600">Reports Pending</p>
              <div className="flex items-center gap-1 text-xs">
                <span className="text-blue-600">Updated today</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview', icon: Shield, showFor: 'both' },
              { id: 'compliance', label: 'Compliance', icon: Shield, showFor: 'both' },
              { id: 'complaints', label: 'Feedback & Complaints', icon: MessageSquare, showFor: 'both' },
              { id: 'care-minutes', label: 'Care Minutes', icon: Clock, showFor: 'agedcare' },
              { id: 'star-ratings', label: 'Star Ratings', icon: Star, showFor: 'agedcare' },
              { id: 'nqip', label: 'NQIP', icon: BarChart3, showFor: 'agedcare' },
              { id: 'sirs', label: 'SIRS', icon: AlertCircle, showFor: 'agedcare' },
              { id: 'audits', label: 'Audits', icon: ClipboardList, showFor: 'both' },
              { id: 'reports', label: 'Quality Reports', icon: FileText, showFor: 'both' },
              { id: 'incidents', label: 'Incidents', icon: AlertTriangle, showFor: 'both' },
              { id: 'metrics', label: 'Metrics', icon: Target, showFor: 'both' },
              { id: 'ai', label: '🤖 AI Insights', icon: BrainIcon, showFor: 'both' }
            ].filter(tab => {
              if (tab.showFor === 'both' || tab.showFor === 'agedcare') return true;
              return false;
            }).map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search reports, incidents, facilities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="Passed">Passed</option>
                <option value="Under Review">Under Review</option>
                <option value="Failed">Failed</option>
                <option value="Completed">Completed</option>
                <option value="Resolved">Resolved</option>
              </select>
              <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <Calendar className="w-4 h-4" />
                Date Range
              </button>
            </div>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'overview' && (
            <OverviewDashboard />
          )}

          {activeTab === 'compliance' && (
            <ComplianceDashboard />
          )}

          {isAgedCare && activeTab === 'care-minutes' && (
            <CareMinutesDashboard />
          )}

          {isAgedCare && activeTab === 'star-ratings' && (
            <StarRatingsDashboard />
          )}

          {isAgedCare && activeTab === 'nqip' && (
            <NQIPDashboard />
          )}

          {isAgedCare && activeTab === 'sirs' && (
            <SIRSDashboard />
          )}

          {activeTab === 'audits' && (
            <AuditsDashboard />
          )}

          {activeTab === 'reports' && (
            <QualityReportsDashboard />
          )}

          {activeTab === 'incidents' && (
            <IncidentsDashboard />
          )}

          {activeTab === 'metrics' && (
            <MetricsDashboard />
          )}

          {activeTab === 'ai' && (
            <div className="space-y-6">
              {/* AI Quality Insights Dashboard */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-100 p-8 rounded-2xl border border-purple-200">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-purple-800 mb-2 flex items-center gap-3">
                      <BrainIcon className="w-6 h-6 text-purple-600" />
                      AI-Powered Quality Insights
                    </h3>
                    <p className="text-purple-600">Intelligent quality monitoring and compliance optimization</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-green-600 font-medium">AI Active</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-green-600">{aiQualityData.optimization.compliance}%</div>
                        <div className="text-sm text-gray-600">Compliance</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Target</span>
                        <span className="text-green-600">95%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full" style={{width: `${aiQualityData.optimization.compliance}%`}}></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                        <TrendingUpIcon className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-blue-600">+{aiQualityData.optimization.improvement}%</div>
                        <div className="text-sm text-gray-600">Improvement</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>This Month</span>
                        <span className="text-blue-600">+{aiQualityData.optimization.improvement}%</span>
                      </div>
                      <div className="text-xs text-gray-500">Quality metrics</div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-purple-600">{aiQualityData.optimization.efficiency}%</div>
                        <div className="text-sm text-gray-600">Efficiency</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Process</span>
                        <span className="text-purple-600">Optimized</span>
                      </div>
                      <div className="text-xs text-gray-500">AI-driven</div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                        <BarChart3Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-green-600">${aiQualityData.optimization.costSavings}K</div>
                        <div className="text-sm text-gray-600">Cost Savings</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>This Quarter</span>
                        <span className="text-green-600">+{aiQualityData.optimization.costSavings}%</span>
                      </div>
                      <div className="text-xs text-gray-500">Through optimization</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Recommendations */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-800">AI Quality Recommendations</h4>
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
                      {aiQualityData.recommendations.length} Active
                    </span>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  {aiQualityData.recommendations.map((recommendation) => (
                    <div key={recommendation.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h5 className="font-semibold text-sm">{recommendation.title}</h5>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              recommendation.priority === 'High' ? 'bg-red-100 text-red-800' :
                              recommendation.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {recommendation.priority}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mb-2">{recommendation.description}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              {recommendation.impact}
                            </span>
                            <span className="flex items-center gap-1">
                              <BrainIcon className="w-3 h-3" />
                              {recommendation.aiConfidence}% confidence
                            </span>
                            <span className="flex items-center gap-1">
                              <Shield className="w-3 h-3" />
                              {recommendation.category}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg">
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quality Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="p-6 border-b border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-800">Quality Trends</h4>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium">Improving</span>
                      </div>
                      <span className="text-sm text-green-600 font-medium">{aiQualityData.analytics.qualityTrends.improving} areas</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-sm font-medium">Stable</span>
                      </div>
                      <span className="text-sm text-blue-600 font-medium">{aiQualityData.analytics.qualityTrends.stable} areas</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <span className="text-sm font-medium">Declining</span>
                      </div>
                      <span className="text-sm text-red-600 font-medium">{aiQualityData.analytics.qualityTrends.declining} areas</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="p-6 border-b border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-800">Compliance Areas</h4>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{aiQualityData.analytics.complianceAreas.excellent}</div>
                      <div className="text-sm text-gray-600 mb-1">Excellent</div>
                      <div className="text-xs text-gray-500">Compliance areas</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">{aiQualityData.analytics.complianceAreas.good}</div>
                      <div className="text-sm text-gray-600 mb-1">Good</div>
                      <div className="text-xs text-gray-500">Compliance areas</div>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{aiQualityData.analytics.complianceAreas.needsImprovement}</div>
                      <div className="text-sm text-gray-600 mb-1">Needs Improvement</div>
                      <div className="text-xs text-gray-500">Compliance areas</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Plus className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold">New Report</h3>
              <p className="text-sm text-gray-600">Create quality report</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Target className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold">Metrics Dashboard</h3>
              <p className="text-sm text-gray-600">View KPIs</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Bell className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold">Alerts</h3>
              <p className="text-sm text-gray-600">View notifications</p>
            </div>
          </div>
        </div>
      </div>

      {/* Notification System */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {notifications.map(notification => (
          <div
            key={notification.id}
            className={`px-4 py-2 rounded-lg shadow-lg text-white ${
              notification.type === 'success' ? 'bg-green-500' :
              notification.type === 'error' ? 'bg-red-500' :
              notification.type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
            }`}
          >
            {notification.message}
          </div>
        ))}
      </div>

      {/* New Report Modal */}
      {showNewReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-emerald-800 flex items-center gap-3">
                <FileText className="w-6 h-6 text-emerald-600" />
                Create Quality Report
              </h2>
              <button 
                onClick={() => setShowNewReportModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                    <option value="comprehensive">Comprehensive Assessment</option>
                    <option value="medication">Medication Safety</option>
                    <option value="infection">Infection Control</option>
                    <option value="resident">Resident Care</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Facility</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                    <option value="sunshine">Sunshine Gardens</option>
                    <option value="oakview">Oakview Manor</option>
                    <option value="riverside">Riverside Care</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Report Title</label>
                <input 
                  type="text" 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Enter report title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 h-24"
                  placeholder="Enter report description..."
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button 
                  onClick={() => setShowNewReportModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    showNotification('Quality report created successfully!', 'success');
                    setShowNewReportModal(false);
                  }}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  Create Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NQIP Report Modal */}
      {showNQIPModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-blue-800 flex items-center gap-3">
                <BarChart3 className="w-6 h-6 text-blue-600" />
                Generate NQIP Report
              </h2>
              <button 
                onClick={() => setShowNQIPModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reporting Period</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="quarterly">Quarterly Report</option>
                    <option value="monthly">Monthly Report</option>
                    <option value="annual">Annual Report</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Facility</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="sunshine">Sunshine Gardens</option>
                    <option value="oakview">Oakview Manor</option>
                    <option value="riverside">Riverside Care</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Include Metrics</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.keys(nqipData.trends).map(metric => (
                    <label key={metric} className="flex items-center">
                      <input type="checkbox" defaultChecked className="mr-2" />
                      <span className="text-sm capitalize">{metric.replace(/([A-Z])/g, ' $1').trim()}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button 
                  onClick={() => setShowNQIPModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    showNotification('NQIP report generated successfully!', 'success');
                    setShowNQIPModal(false);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Generate Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SIRS Report Modal */}
      {showSIRSModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-red-800 flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-red-600" />
                Report SIRS Incident
              </h2>
              <button 
                onClick={() => setShowSIRSModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Incident Type</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500">
                    <option value="abuse">Physical Abuse</option>
                    <option value="neglect">Neglect</option>
                    <option value="exploitation">Exploitation</option>
                    <option value="sexual">Sexual Assault</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Resident</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500">
                    <option value="">Select resident...</option>
                    <option value="john">John Smith</option>
                    <option value="mary">Mary Wilson</option>
                    <option value="robert">Robert Johnson</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Facility</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500">
                    <option value="sunshine">Sunshine Gardens</option>
                    <option value="oakview">Oakview Manor</option>
                    <option value="riverside">Riverside Care</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Incident Description</label>
                <textarea 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 h-24"
                  placeholder="Provide detailed description of the incident..."
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button 
                  onClick={() => setShowSIRSModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    showNotification('SIRS incident reported successfully!', 'success');
                    setShowSIRSModal(false);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Report Incident
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audit Modal */}
      {showAuditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-purple-800 flex items-center gap-3">
                <ClipboardList className="w-6 h-6 text-purple-600" />
                Schedule Audit
              </h2>
              <button 
                onClick={() => setShowAuditModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Audit Type</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                    <option value="medication">Medication Management</option>
                    <option value="infection">Infection Control</option>
                    <option value="resident">Resident Care</option>
                    <option value="safety">Safety & Security</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Auditor</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                    <option value="jennifer">Dr. Jennifer Lee</option>
                    <option value="sarah">Nurse Sarah Johnson</option>
                    <option value="david">David Brown</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Scheduled Date</label>
                  <input 
                    type="date" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    defaultValue={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Facility</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                    <option value="sunshine">Sunshine Gardens</option>
                    <option value="oakview">Oakview Manor</option>
                    <option value="riverside">Riverside Care</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Audit Scope</label>
                <textarea 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-24"
                  placeholder="Describe the scope and objectives of this audit..."
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button 
                  onClick={() => setShowAuditModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    showNotification('Audit scheduled successfully!', 'success');
                    setShowAuditModal(false);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Schedule Audit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Reporting Modal */}
      {showComplianceReporting && (
        <ComplianceReporting onClose={() => setShowComplianceReporting(false)} />
      )}
    </div>
  );
};

export default Quality;
